import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArtistDTO, Song } from "../../spotipitan/types";
import { Spotip17anFunctions } from "../../../../utils/api";
import ArtistProfileHeader from "../../../shared/artist-profile-header/artist-profile-header";
import { SongList } from "../../spotipitan/song-list";
import useSongStore from "../../spotipitan/store/SongState";
import { DiscordBotApi } from "../../../../utils/discordBotApi";
import SeparatorLine from "../../../shared/separator-line/separator-line";
import useUserStore from "../../../../store/UserState";
import ItemsHorizontalScroll from "../../../squadDiskShared/itemsHorizontalScroll/itemsHorizontalScroll";
import ItemDetailsToView from "../../../squadDiskShared/itemDetailsView/itemDetailsToView";
import SquareImage from "../../../shared/square-image/square-image";
import { AlbumEditModal } from "../../../shared/album-edit-modal";
import toast from "react-hot-toast";
import './spotip17an-artist.scss';

const Spotip17anArtist = () => {
    const { id } = useParams();
    const [artist, setArtist] = useState<ArtistDTO>();
    const [loading, setLoading] = useState(true);
    const loadStatus = useRef<"none" | "loading" | "loaded">("none");
    const navigate = useNavigate();
    const songStore = useSongStore(state => state);
    const { user, spotip17anUser, offlineMode } = useUserStore(state => state.state);
    const [songs, setSongs] = useState<Song[]>([]);
    const [showCreateAlbumModal, setShowCreateAlbumModal] = useState(false);
    const loadedId = useRef<string>("");
    const [statusError, setStatusError] = useState<number>(0);
    const contentRef = useRef<HTMLDivElement>(null);
    const [showSecondaryHeader, setShowSecondaryHeader] = useState(false);
    const [showAllSongs, setShowAllSongs] = useState(false);

    const isThisArtist = Boolean(
        user?.uid && (
            (artist?.creator_uid && artist.creator_uid === user.uid) ||
            (artist?.spotip17anUid && artist.spotip17anUid === user.uid) ||
            (spotip17anUser && artist?.id && spotip17anUser.id === Number(artist.id))
        )
    );

    useEffect(() => {
        if (offlineMode || !id || !user?.uid) {
            return;
        }

        Spotip17anFunctions.use('addToPlaylistHistory').post({ playlistId: id }).catch(() => {
            console.error("Error adding to playlist history");
        });
    }, [id, offlineMode, user?.uid]);

    useEffect(() => {
        if (!id) {
            navigate("/");
            return;
        }

        let isCancelled = false;
        setArtist(undefined);
        setSongs([]);
        setLoading(true);
        setStatusError(0);
        loadStatus.current = "loading";

        Spotip17anFunctions.use('fetchPlaylist').call({ playlistId: id }).then((res: ArtistDTO) => {
            if (isCancelled) return;
            setArtist(res);
            setSongs(res?.songs ?? []);
            setLoading(false);
            loadStatus.current = "loaded";
            loadedId.current = id;
            setStatusError(0);
        }).catch((e) => {
            if (isCancelled) return;
            setLoading(false);
            setStatusError(e?.code || 500);
            loadStatus.current = "none";
            console.error("Error fetching artist", e);
        });

        return () => {
            isCancelled = true;
            loadStatus.current = "none";
        };
    }, [id, navigate, offlineMode]);

    useEffect(() => {
        const container = contentRef.current;
        const handleScroll = () => {
            if ((container?.scrollTop || 0) > 180) {
                setShowSecondaryHeader(true);
            } else {
                setShowSecondaryHeader(false);
            }
        };

        container?.addEventListener("scroll", handleScroll);
        return () => {
            container?.removeEventListener("scroll", handleScroll);
        };
    }, []);

    const handleClickPlay = (songsToPlay: Song[]) => {
        if (songStore.state.isBotMode && songStore.state.botGuildId && id) {
            (async () => {
                try {
                    await DiscordBotApi.stop(songStore.state.botGuildId!);
                } catch (e) {
                    console.warn("Could not stop bot before playing artist:", e);
                }
                DiscordBotApi.play(
                    songStore.state.botGuildId!,
                    `https://spotip17an.es/artist/${id}`,
                    songStore.state.botVoiceChannelId || undefined
                ).then(() => {
                    toast.success("Colección enviada al bot");
                }).catch(e => console.error("Error playing in Bot Mode:", e));
            })();
            return;
        }

        songStore.emptyQueue();
        songStore.setCurrentSong(undefined);
        songStore.setQueue(songsToPlay);
        songStore.playNextSong();
        songStore.emptyHistory();
    };

    const visibleSongs = useMemo(() => {
        if (showAllSongs || songs.length <= 5) {
            return songs;
        }
        return songs.slice(0, 5);
    }, [songs, showAllSongs]);

    const discographyItems = useMemo(() => {
        return (artist?.playlists || []).map((album) => {
            const desc = album.songs_count
                ? (album.songs_count == 1 ? "Single" : `${album.songs_count} canciones`)
                : "Álbum";

            return (
                <ItemDetailsToView
                    key={album.id}
                    description={desc}
                    title={album.username || album.name || "Álbum"}
                    onClick={() => navigate(`/playlist/${album.id}`)}
                    showHover={true}
                    iconClass=""
                    image={album?.poster_url || <SquareImage playlist={album} />}
                />
            );
        });
    }, [artist?.playlists, navigate]);

    if (statusError) {
        return (
            <div className="container">
                <h2 className="text-center mt-5">No se encontró el artista</h2>
                <p className="text-center">Puede que haya sido eliminado o no exista.</p>
                <div className="d-flex justify-content-center">
                    <button className="btn btn-primary mt-3" onClick={() => navigate("/")}>
                        Volver al inicio
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container spotip17an-artist" ref={contentRef}>
            <div className={`spotip17an-artist__secondary-header ${showSecondaryHeader ? "show" : "hide"}`}>
                <ArtistProfileHeader
                    onClickImage={() => {
                        contentRef.current?.scrollTo({
                            top: 0,
                            behavior: 'smooth'
                        });
                    }}
                    summarySize={50}
                    summaryHeader={true}
                    artist={artist}
                    loading={loading}
                    handleClickPlay={handleClickPlay}
                />
            </div>

            <ArtistProfileHeader
                artist={artist}
                loading={loading}
                handleClickPlay={handleClickPlay}
            />

            <SeparatorLine className="mt-2 mb-4" />

            {/* SECCIÓN TOP CANCIONES POPULARES */}
            {!!songs.length && (
                <div className="mb-4">
                    <h3 className="spotip17an-artist__section-title">
                        <i className="fa-solid fa-fire"></i>
                        Canciones populares
                    </h3>
                    <SongList
                        showSearch={false}
                        showIndex={true}
                        songs={visibleSongs}
                        loadingSongs={loading}
                        canScrollToQueue
                        style={{ paddingBottom: 0 }}
                    />
                    {songs.length > 5 && (
                        <div className="d-flex justify-content-start">
                            <button
                                className="spotip17an-artist__toggle-btn"
                                onClick={() => setShowAllSongs(!showAllSongs)}
                            >
                                {showAllSongs ? "Mostrar menos" : "Ver más canciones"}
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* SECCIÓN DISCOGRAFÍA / ÁLBUMES Y SENCILLOS */}
            {(!!artist?.playlists?.length || isThisArtist) && (
                <div className="mb-4">
                    <SeparatorLine className="mt-4 mb-4" />
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h3 className="spotip17an-artist__section-title mb-0">
                            <i className="fa-solid fa-compact-disc"></i>
                            Discografía
                        </h3>
                        {isThisArtist && (
                            <button
                                className="nice-button nice-button-sm d-flex gap-1 align-items-center"
                                onClick={() => setShowCreateAlbumModal(true)}
                                title="Crear nuevo álbum"
                            >
                                <i className="fa-solid fa-plus"></i>
                                <span>Crear álbum</span>
                            </button>
                        )}
                    </div>
                    {artist?.playlists?.length ? (
                        <div style={{ height: '175px' }}>
                            <ItemsHorizontalScroll key={artist?.id || id} className="container spotipitan-home__items-container">
                                {discographyItems}
                            </ItemsHorizontalScroll>
                        </div>
                    ) : (
                        <div className="text-muted small py-2">
                            Aún no has publicado ningún álbum. ¡Crea el primero con el botón superior!
                        </div>
                    )}
                </div>
            )}
            <AlbumEditModal
                show={showCreateAlbumModal}
                onClose={() => setShowCreateAlbumModal(false)}
                onSave={(newAlbum) => {
                    setArtist(prev => prev ? {
                        ...prev,
                        playlists: [newAlbum as any, ...(prev.playlists || [])]
                    } : prev);
                }}
            />
        </div>
    );
};

export default Spotip17anArtist;
